import { Table as TanstackTable } from '@tanstack/react-table';
import clsx from 'clsx';
import { ButtonWithIcon } from 'src/components/Buttons';
import { FormatContent } from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { ChevronEndIconV2 } from 'src/svgs/Viagogo/goicons/ChevronEndIconV2';
import { ChevronLeftIconV3 } from 'src/svgs/Viagogo/goicons/ChevronLeftIconV3';
import { ChevronRightIconV3 } from 'src/svgs/Viagogo/goicons/ChevronRightIconV3';
import { ChevronStartIconV2 } from 'src/svgs/Viagogo/goicons/ChevronStartIconV2';
import { FormatContentId } from 'src/utils/constants/formatContentId';

import { usePagination, UsePaginationOptions } from '../hooks/usePagination';
import { TablePaginationVariant } from '../types';
import * as styles from './TablePagination.css';

// Navigation button component
interface NavigationButtonProps {
  icon: React.ReactNode;
  onClick: () => void;
  disabled: boolean;
  ariaLabel: string;
}

const NavigationButton = ({
  icon,
  onClick,
  disabled,
  ariaLabel,
}: NavigationButtonProps) => (
  <ButtonWithIcon
    className={styles.navigationButton}
    variant={'icon'}
    size={'unset'}
    textColor={'icon'}
    shape={'rect'}
    onClick={onClick}
    icon={icon}
    disabled={disabled}
    aria-label={ariaLabel}
  />
);

/**
 * Returns an array of page numbers (and 'ellipsis' sentinels) to display.
 *
 * Rules:
 * - When totalPages ≤ 7: show all pages, no ellipsis.
 * - Otherwise show a window of 7 consecutive pages centred on currentPage,
 *   clamped so the window stays inside [1, totalPages].
 *   A leading ellipsis is added when page 1 is not in the window.
 *   A trailing ellipsis is added when the last page is not in the window.
 */
const PAGE_WINDOW_SIZE = 7;

export function getVisiblePages(
  currentPage: number,
  totalPages: number
): (number | 'ellipsis')[] {
  if (totalPages <= PAGE_WINDOW_SIZE) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const windowSize = PAGE_WINDOW_SIZE;
  // Centre the window on currentPage, then clamp to valid range
  let windowStart = currentPage - Math.floor(windowSize / 2);
  let windowEnd = windowStart + windowSize - 1;

  if (windowStart < 1) {
    windowStart = 1;
    windowEnd = windowSize;
  }
  if (windowEnd > totalPages) {
    windowEnd = totalPages;
    windowStart = totalPages - windowSize + 1;
  }

  const pages: (number | 'ellipsis')[] = [];

  if (windowStart > 1) {
    pages.push('ellipsis');
  }

  for (let p = windowStart; p <= windowEnd; p++) {
    pages.push(p);
  }

  if (windowEnd < totalPages) {
    pages.push('ellipsis');
  }

  return pages;
}

export interface TablePaginationProps<T = any> extends UsePaginationOptions {
  table: TanstackTable<T>;
  className?: string;
  variant?: TablePaginationVariant;
  totalCount?: number | null;
  footerCta?: React.ReactNode;
}

export function TablePagination<T = any>({
  table,
  pageSize: defaultPageSize = 10,
  className,
  variant = 'default',
  totalCount,
  footerCta,
  ...paginationOptions
}: TablePaginationProps<T>) {
  const pagination = usePagination(table, {
    pageSize: defaultPageSize,
    ...paginationOptions,
  });

  const {
    currentPage,
    totalPages,
    canNextPage,
    goToPage,
    previousPage,
    nextPage,
    goToFirstPage,
    goToLastPage,
  } = pagination;

  return (
    <div className={clsx(styles.paginationContainer, className)}>
      <Stack alignItems="center" gap="s">
        {footerCta && (
          <div style={{ marginLeft: vars.spacing.sm }}>{footerCta}</div>
        )}

        <div className={styles.paginationControls}>
          <NavigationButton
            onClick={goToFirstPage}
            disabled={currentPage === 1}
            ariaLabel="Go to first page"
            icon={
              <ChevronStartIconV2
                size={vars.iconSize.s}
                width={'12px'}
                height={'12px'}
              />
            }
          />

          <NavigationButton
            onClick={previousPage}
            disabled={currentPage === 1}
            ariaLabel="Go to previous page"
            icon={
              <ChevronLeftIconV3
                size={vars.iconSize.s}
                fill={vars.color.iconPrimary}
                width={'12px'}
                height={'12px'}
              />
            }
          />

          <div className={styles.pageIndicator}>
            {variant === 'input' ? (
              <>
                {getVisiblePages(currentPage, totalPages).map((page, index) =>
                  page === 'ellipsis' ? (
                    <span
                      key={`ellipsis-${index}`}
                      className={styles.ellipsisText}
                      aria-hidden="true"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      className={clsx(styles.pageNumberButton, {
                        [styles.pageNumberButtonActive]: page === currentPage,
                      })}
                      onClick={() => page !== currentPage && goToPage(page)}
                      aria-label={`Go to page ${page}`}
                      aria-current={page === currentPage ? 'page' : undefined}
                    >
                      {page}
                    </button>
                  )
                )}
              </>
            ) : (
              <div className={styles.pageDisplayText}>
                <FormatContent
                  id={FormatContentId.PageOfPages}
                  params={[`${currentPage}`, `${totalPages}`]}
                />
              </div>
            )}
          </div>

          <NavigationButton
            onClick={nextPage}
            disabled={!canNextPage}
            ariaLabel="Go to next page"
            icon={
              <ChevronRightIconV3
                size={vars.iconSize.s}
                fill={vars.color.iconPrimary}
                width={'12px'}
                height={'12px'}
              />
            }
          />

          {variant !== 'input' && (
            <NavigationButton
              onClick={goToLastPage}
              disabled={!canNextPage}
              ariaLabel="Go to last page"
              icon={
                <ChevronEndIconV2
                  size={vars.iconSize.s}
                  fill={vars.color.iconPrimary}
                  width={'12px'}
                  height={'12px'}
                />
              }
            />
          )}
        </div>

        {totalCount != null && (
          <div className={styles.totalCountText}>
            {totalCount.toLocaleString()} results
          </div>
        )}
      </Stack>
    </div>
  );
}
